/**
 * ReportBriefsTab — admin governance of the public report briefs (3/9 SEO):
 * status counts + backfill, per-report preview / regenerate / hide, and an
 * inline editor for title, category, access and the brief text sections.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Eye, EyeOff, ExternalLink, Pencil, Check, X, Play, Sparkles, AlertTriangle } from "lucide-react";
import { TabsContent } from "../ui/tabs";
import { API } from "../../config";

const ACCESS = ["Free", "Free PDF", "Registration", "Paid", "Not specified"];
const TYPES = ["Report", "White paper", "Survey", "Index", "Barometer", "Forecast", "Study", "Analysis", "Policy paper", "Factsheet"];

function BriefEditor({ dm, row, authHeader, onSaved, onCancel }) {
  const [full, setFull] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const input = `w-full px-3 py-2 rounded-lg text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`;
  const lbl = `text-[11px] font-medium block mb-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`;

  useEffect(() => {
    axios.get(`${API}/reports/by-slug/${row.slug}`).then(({ data }) => {
      const b = data.brief || {};
      setFull(data);
      setForm({
        clean_title: data.report.clean_title || "", category_slug: data.report.category_slug, access: b.access || data.report.access || "Not specified",
        report_type: b.report_type || data.report.report_type || "Report",
        at_a_glance: (b.at_a_glance || []).join("\n"), scope: b.scope || "", methodology: b.methodology || "", abstract: b.abstract || data.report.summary || "",
        key_findings: (b.key_findings || []).join("\n\n"),
        why_it_matters: (b.why_it_matters || []).map((w) => `${w.audience}: ${w.text}`).join("\n"),
      });
    }).catch(() => toast.error("Could not load the brief"));
  }, [row.slug]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        clean_title: form.clean_title, category_slug: form.category_slug, access: form.access, report_type: form.report_type,
        abstract: form.abstract, scope: form.scope, methodology: form.methodology,
        at_a_glance: form.at_a_glance.split("\n").map((s) => s.trim()).filter(Boolean),
        key_findings: form.key_findings.split(/\n\s*\n/).map((s) => s.replace(/\s+/g, " ").trim()).filter(Boolean),
        why_it_matters: form.why_it_matters.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
          const i = l.indexOf(":"); return i > 0 ? { audience: l.slice(0, i).trim(), text: l.slice(i + 1).trim() } : { audience: "Industry", text: l };
        }),
      };
      await axios.patch(`${API}/admin/reports/${row.id}/brief`, payload, { headers: authHeader() });
      toast.success("Brief saved");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  if (!form) return <div className="py-4 flex justify-center"><RefreshCw className="w-4 h-4 animate-spin" /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className={`mt-3 p-4 rounded-xl space-y-3 ${dm ? "bg-[#0c1011]" : "bg-white"}`} data-testid={`brief-editor-${row.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
        <div><label className={lbl}>Clean title (SEO)</label><input value={form.clean_title} onChange={set("clean_title")} className={input} data-testid="brief-edit-title" /></div>
        <div><label className={lbl}>Category</label>
          <select value={form.category_slug} onChange={set("category_slug")} className={input} data-testid="brief-edit-category">
            {(full?.categories || row.categories || []).map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Access</label><select value={form.access} onChange={set("access")} className={input} data-testid="brief-edit-access">{ACCESS.map((a) => <option key={a}>{a}</option>)}</select></div>
        <div><label className={lbl}>Type</label><select value={form.report_type} onChange={set("report_type")} className={input} data-testid="brief-edit-type">{TYPES.map((a) => <option key={a}>{a}</option>)}</select></div>
      </div>
      <div><label className={lbl}>At a glance (one bullet per line)</label><textarea rows={4} value={form.at_a_glance} onChange={set("at_a_glance")} className={input} data-testid="brief-edit-glance" /></div>
      <div><label className={lbl}>Abstract (library summary, complete sentences)</label><textarea rows={3} value={form.abstract} onChange={set("abstract")} className={input} data-testid="brief-edit-abstract" /></div>
      <div><label className={lbl}>What the report covers</label><textarea rows={2} value={form.scope} onChange={set("scope")} className={input} data-testid="brief-edit-scope" /></div>
      <div><label className={lbl}>Key findings (one paragraph per blank line)</label><textarea rows={7} value={form.key_findings} onChange={set("key_findings")} className={input} data-testid="brief-edit-findings" /></div>
      <div><label className={lbl}>Why it matters (one line per audience: &quot;Audience: text&quot;)</label><textarea rows={3} value={form.why_it_matters} onChange={set("why_it_matters")} className={input} data-testid="brief-edit-why" /></div>
      <div><label className={lbl}>Methodology and limits</label><textarea rows={2} value={form.methodology} onChange={set("methodology")} className={input} data-testid="brief-edit-methodology" /></div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="brief-edit-save" className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
          {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
        </button>
        <button onClick={onCancel} data-testid="brief-edit-cancel" className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${dm ? "bg-white/5 text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}><X className="w-3 h-3" /> Cancel</button>
      </div>
    </div>
  );
}

export default function ReportBriefsTab({ dm, authHeader }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [backfill, setBackfill] = useState({});
  const [lengthTarget, setLengthTarget] = useState({ min: 600, max: 900 });
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports/briefs`, { headers: authHeader() });
      setRows(data.briefs || []); setCounts(data.counts || {}); setBackfill(data.backfill || {}); setCategories(data.categories || []);
      if (data.length_target) setLengthTarget(data.length_target);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load briefs"); } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!backfill.running) return undefined;
    const id = setInterval(() => load(true), 10000);
    return () => clearInterval(id);
  }, [backfill.running, load]);

  const startBackfill = async (includeFailed) => {
    setActing("backfill");
    try {
      const { data } = await axios.post(`${API}/admin/reports/briefs/backfill?include_failed=${includeFailed}`, {}, { headers: authHeader() });
      data.started ? toast.success(data.message) : toast.info(data.reason || "Already running");
      setTimeout(() => load(true), 1500);
    } catch (err) { toast.error(err.response?.data?.detail || "Backfill failed to start"); } finally { setActing(null); }
  };

  const regenerate = async (row) => {
    setActing(row.id);
    try {
      const { data } = await axios.post(`${API}/admin/reports/${row.id}/brief/regenerate`, {}, { headers: authHeader() });
      toast.success(`Brief regenerated — ${data.word_count} words${data.partial_source ? " (partial source)" : ""}`);
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Regeneration failed"); } finally { setActing(null); }
  };

  const toggleHidden = async (row) => {
    setActing(row.id);
    try {
      await axios.patch(`${API}/admin/reports/${row.id}/brief`, { hidden: !row.hidden }, { headers: authHeader() });
      toast.success(row.hidden ? "Brief visible again" : "Brief hidden from the public page (metadata stays)");
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); } finally { setActing(null); }
  };

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const pill = (color) => `px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${
    color === "green" ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700")
      : color === "red" ? (dm ? "bg-rose-400/15 text-rose-300" : "bg-rose-100 text-rose-700")
        : color === "amber" ? (dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700")
          : (dm ? "bg-slate-700 text-slate-400" : "bg-slate-200 text-slate-500")}`;
  const iconBtn = `p-2 rounded-lg transition-colors disabled:opacity-40 ${dm ? "hover:bg-white/5 text-slate-400" : "hover:bg-black/5 text-[#6b7a7d]"}`;
  const isShort = (r) => r.short || (r.brief_status === "generated" && (r.word_count || 0) < (lengthTarget.min || 600) && !r.partial_source);
  const visible = rows.filter((r) => !filter || (filter === "short" ? isShort(r) : r.brief_status === filter));

  return (
    <TabsContent value="report-briefs">
      <div className="max-w-4xl space-y-6" data-testid="report-briefs-tab">
        <div>
          <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Report Briefs</h2>
          <p className={`text-sm mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Public /reports/&#123;slug&#125; pages with TourismIntel&apos;s editorial brief (Claude Haiku 5.5, source fetched directly — no Firecrawl).
            Published automatically; edit, regenerate or hide here.
          </p>
        </div>

        <div className={card} data-testid="briefs-status-card">
          <div className="flex items-center flex-wrap gap-3">
            {["generated", "short", "pending", "failed", "generating"].map((s) => (
              <button key={s} onClick={() => setFilter(filter === s ? "" : s)} data-testid={`briefs-filter-${s}`}
                title={s === "short" ? `Generated below the ${lengthTarget.min}-word target (rich source) — regenerated by the backfill` : undefined}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 ${filter === s ? (dm ? "bg-cyan-400/20 text-cyan-200" : "bg-[#006874]/15 text-[#006874]") : (dm ? "bg-[#0c1011] text-slate-300" : "bg-[#f3f4f5] text-[#3b494c]")}`}>
                {s} <span className={pill(s === "generated" ? "green" : s === "failed" ? "red" : s === "generating" || s === "short" ? "amber" : "slate")} data-testid={`briefs-count-${s}`}>{counts[s] || 0}</span>
              </button>
            ))}
            <span className={`text-xs ml-auto ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid="briefs-total">
              {counts.total || 0} published reports{counts.partial ? <span data-testid="briefs-partial-share"> · {counts.partial} source-limited ({Math.round((counts.partial / Math.max(counts.total || 1, 1)) * 100)}%)</span> : null}
            </span>
          </div>
          <div className="mt-4 flex items-center flex-wrap gap-2">
            <button onClick={() => startBackfill(false)} disabled={acting === "backfill" || backfill.running} data-testid="briefs-backfill-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
              <Play className="w-3 h-3" /> Generate missing &amp; upgrade short briefs
            </button>
            <button onClick={() => startBackfill(true)} disabled={acting === "backfill" || backfill.running} data-testid="briefs-backfill-failed-btn"
              className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 disabled:opacity-50 ${dm ? "bg-white/5 text-slate-300" : "bg-[#f3f4f5] text-[#3b494c]"}`}>
              <RefreshCw className="w-3 h-3" /> Retry failed too
            </button>
            {backfill.running && (
              <span className={`${pill("amber")} flex items-center gap-1`} data-testid="briefs-backfill-running">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> backfill running · {backfill.done} done · {backfill.failed} failed
              </span>
            )}
            <button onClick={() => load()} disabled={loading} data-testid="briefs-refresh-btn" className={iconBtn} title="Refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
          </div>
          <p className={`text-[11px] mt-3 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Target {lengthTarget.min}–{lengthTarget.max} words per brief (rich sources; thin/gated sources stay shorter and are flagged). ~€0.02 per brief (Haiku, incl. one length pass).
            Hard rules enforced in the pipeline: only figures present in the source text, no long quotes, official link always shown.
          </p>
        </div>

        <div className={card} data-testid="briefs-list-card">
          {loading ? <div className="flex justify-center py-6"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
            : visible.length === 0 ? <p className={`text-sm py-2 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid="briefs-empty">No reports match this filter.</p>
              : (
                <div className="space-y-2">
                  {visible.map((r) => (
                    <div key={r.id} data-testid={`brief-row-${r.id}`} className={`p-3 rounded-xl ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`} title={r.title}>{r.clean_title}</p>
                          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                            <span className={pill("slate")}>{r.organisation}</span>
                            <span className={pill("slate")}>{r.category_label}</span>
                            <span className={pill(r.brief_status === "generated" ? "green" : r.brief_status === "failed" ? "red" : r.brief_status === "generating" ? "amber" : "slate")} data-testid={`brief-status-${r.id}`}>{r.brief_status}</span>
                            {r.hidden && <span className={pill("red")} data-testid={`brief-hidden-${r.id}`}>hidden</span>}
                            {r.partial_source && <span className={`${pill("amber")} flex items-center gap-1`} title={r.source_kind === "none" ? `Source not retrievable${r.source_http_status ? ` (HTTP ${r.source_http_status})` : ""} — brief written from library metadata only` : "Generated from a partial/gated source"} data-testid={`brief-partial-${r.id}`}><AlertTriangle className="w-2.5 h-2.5" /> {r.source_kind === "none" ? `source unreachable${r.source_http_status ? ` · ${r.source_http_status}` : ""}` : "partial source"}</span>}
                            {r.edited_by_admin && <span className={pill("slate")}>edited</span>}
                            {r.word_count ? <span className={pill(isShort(r) ? "amber" : "slate")} data-testid={`brief-words-${r.id}`}>{r.word_count} words{isShort(r) ? ` · below ${lengthTarget.min}` : ""}</span> : null}
                            {r.access && r.access !== "Not specified" && <span className={pill("slate")}>{r.access}</span>}
                          </div>
                          {r.error && <p className={`text-[11px] mt-1 ${dm ? "text-rose-400" : "text-rose-600"}`}>{r.error}</p>}
                        </div>
                        <div className="flex gap-0.5 flex-shrink-0">
                          <a href={`/reports/${r.slug}`} target="_blank" rel="noreferrer" title="Preview public page" data-testid={`brief-preview-${r.id}`} className={iconBtn}><ExternalLink className="w-4 h-4" /></a>
                          <button onClick={() => setEditing(editing === r.id ? null : r.id)} disabled={acting === r.id} title="Edit title / category / access / text" data-testid={`brief-edit-${r.id}`} className={iconBtn}><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => regenerate(r)} disabled={acting === r.id} title="Regenerate with Claude" data-testid={`brief-regenerate-${r.id}`} className={iconBtn}>
                            {acting === r.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          </button>
                          {r.brief_status === "generated" && (
                            <button onClick={() => toggleHidden(r)} disabled={acting === r.id} title={r.hidden ? "Show brief" : "Hide brief from the public page"} data-testid={`brief-toggle-hidden-${r.id}`} className={iconBtn}>
                              {r.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </div>
                      {editing === r.id && <BriefEditor dm={dm} row={{ ...r, categories }} authHeader={authHeader} onSaved={() => { setEditing(null); load(true); }} onCancel={() => setEditing(null)} />}
                    </div>
                  ))}
                </div>
              )}
        </div>
      </div>
    </TabsContent>
  );
}
